import { Wheel } from 'react-custom-roulette';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { SpinReward, SpinResult } from '../types';
import { freeSpinWithReward, getRandomFreeReward } from '../services/api';
import { colors } from '../theme/themeConfig';
import PageSwitcher from '../components/PageSwitcher';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
// Import contexts and components
import { useUser } from '../contexts/UserContext';
import { useSpinRewards } from '../contexts/SpinRewardsContext';
import PageHeader from '../components/PageHeader';

// Color palette - updated to use muted colors based on the new theme
const wheelColors = {
  // primary: '#4CAF50',    // Muted green (from updated theme)
  // secondary: '#26A69A',  // Teal (from updated theme)
  primary: '#FF0000',    // RED DEATH
  secondary: '#FFAA1D',  // YELLOW DEATH
  border: '#1E3A4A',     // Warmer dark border (from updated theme)
  center: 'white',
  pointer: 'white',
  text: 'white',
  textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
};

const FreeWheelPage: React.FC = () => {
  // Get user data and rewards from contexts
  const { user, telegramUser, isLoading: userLoading, refreshUser, lastFreeSpin, lastSpinResult, refreshLastFreeSpin } = useUser();
  const { freeRewards, isLoading: rewardsLoading } = useSpinRewards();
  
  // Combine loading states with timeout to prevent infinite loading
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Update loading state based on context loading states
    setLoading(userLoading || rewardsLoading);
    
    // Add a timeout to prevent infinite loading
    const timer = setTimeout(() => {
      if (loading && (Date.now() - loadStartTime) > 5000) {
        setLoading(false);
      }
    }, 5000);
    
    return () => clearTimeout(timer);
  }, [userLoading, rewardsLoading]);
  
  // Track when loading started
  const [loadStartTime] = useState(Date.now());
  
  // State for wheel
  const [segments, setSegments] = useState<string[]>([]);
  const [wheelData, setWheelData] = useState<any[]>([]);
  const [selectedReward, setSelectedReward] = useState<SpinReward | null>(null);
  const [spinResult, setSpinResult] = useState<SpinResult | null>(null);
  
  // State for wheel spinning
  const [mustSpin, setMustSpin] = useState(false);
  const [prizeNumber, setPrizeNumber] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  
  // State for wheel animation - to implement the specific animation requirements
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [wheelRotation, setWheelRotation] = useState(0);
  
  // State for insufficient balance popup
  const [showInsufficientBalancePopup, setShowInsufficientBalancePopup] = useState(false);
  
  // State for cooldown information
  const [cooldownTimeRemaining, setCooldownTimeRemaining] = useState('');
  
  // Cooldown period in hours
  const FREE_SPIN_COOLDOWN_HOURS = 24;
  
  // Default user ID to use as fallback
  const DEFAULT_USER_ID = 7345174833;
  
  // Mock data for testing
  const MOCK_BONUS_BALANCE = 1000;
  const spinCost = 100;
  
  // Using single breakpoint at 375px as per user preference
  const isMobile = useMediaQuery('(max-width: 375px)');
  const username = telegramUser?.username || user?.telegramUsername || 'ggk_1';
  const navigate = useNavigate();
  const theme = useTheme();

  // Update wheel data when free rewards change
  useEffect(() => {
    if (freeRewards && freeRewards.length > 0) {
      updateWheelData();
    }
  }, [freeRewards]);
  
  // Check cooldown and set last spin result when component mounts
  useEffect(() => {
    if (lastFreeSpin && lastSpinResult) {
      const lastSpinDate = new Date(lastFreeSpin);
      const now = new Date();
      const diffHours = (now.getTime() - lastSpinDate.getTime()) / (1000 * 60 * 60);
      
      if (diffHours < FREE_SPIN_COOLDOWN_HOURS) {
        // Calculate remaining time for reference
        const remainingHours = Math.floor(FREE_SPIN_COOLDOWN_HOURS - diffHours);
        const remainingMinutes = Math.floor((FREE_SPIN_COOLDOWN_HOURS - diffHours - remainingHours) * 60);
        const remainingSeconds = Math.floor(((FREE_SPIN_COOLDOWN_HOURS - diffHours - remainingHours) * 60 - remainingMinutes) * 60);
        
        const formattedTime = `${remainingHours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        setCooldownTimeRemaining(formattedTime);
        
        let result: SpinResult = {
          success: true,
          message: 'Congratulations!',
          reward: {
            type: 'rp',
            amount: lastSpinResult,
            minPurchase: 0,
          }
        };

        // Show the last spin result
        setSpinResult(result);
      }
    }
  }, [lastFreeSpin, lastSpinResult]);

  const updateWheelData = () => {
    if (!freeRewards || freeRewards.length === 0) return;
      
    // Prepare wheel segments and colors
    const wheelItems: any[] = [];
    
    // Color palette for alternating colors - using only 2 colors as requested
    const colorPalette = [
      wheelColors.primary,
      wheelColors.secondary,
    ];
    
    // Create segments directly from cherry-picked rewards
    freeRewards.forEach((reward, index) => {
      // Format segment text
      const segmentText = reward.rewardType === 'uc' 
        ? `${reward.rewardAmount} UC` 
        : `${reward.rewardAmount} рефбаллов`;
      
      // Add to wheel data with alternating colors
      wheelItems.push({
        option: segmentText,
        style: { backgroundColor: colorPalette[index % colorPalette.length] }
      });
    });
    
    // Set wheel data for the component
    setWheelData(wheelItems);
  };

  // Function to select a random reward and set the prize number
  const selectRandomReward = async () => {
    // Don't proceed if rewards array is empty
    if (!freeRewards || freeRewards.length === 0) {
      console.error('Cannot select random reward - rewards array is empty');
      return;
    }
    
    // Get a random reward based on probability
    // const randomReward = getRandomReward(freeRewards);
    const randomReward = await getRandomFreeReward();
    
    // Store the selected reward
    setSelectedReward(randomReward);
    
    if (randomReward) {
      // Find the segment text for this reward
      const segmentText = randomReward.rewardType === 'uc' 
        ? `${randomReward.rewardAmount} UC` 
        : `${randomReward.rewardAmount} рефбаллов`;
      
      // Find the index of this segment in the wheel data
      const segmentIndex = wheelData.findIndex(item => item.option === segmentText);
      
      if (segmentIndex !== -1) {
        setPrizeNumber(segmentIndex);
      } else {
        console.error('WARNING: Segment not found in wheel data!', {
          segment: segmentText,
          availableSegments: wheelData.map(item => item.option)
        });
      }
    }
  };

  const _getRandomReward = (rewardsList: SpinReward[]) => {
    if (!rewardsList || rewardsList.length === 0) {
      console.error('No rewards available');
      return null;
    }
    
    // Calculate total probability
    const totalProbability = rewardsList.reduce((sum, reward) => sum + +reward.probability, 0);
    
    // Generate random number between 0 and total probability
    const random = Math.random() * totalProbability;
    
    // Find the reward based on probability
    let cumulativeProbability = 0;
    for (const reward of rewardsList) {
      cumulativeProbability += +reward.probability;
      if (random <= cumulativeProbability) {
        return reward;
      }
    }
    
    // Fallback to the last reward if something goes wrong
    console.error('No reward selected, using last reward as fallback');
    return rewardsList[rewardsList.length - 1];
  };

  const handleSpinClick = async () => {
    // Check if the user is on cooldown
    if (lastFreeSpin) {
      const lastSpinDate = new Date(lastFreeSpin); // Assuming lastFreeSpin is an ISO string from backend
      const now = new Date();

      // Get time values in UTC to avoid timezone issues
      const lastSpinUTC = Date.UTC(
        lastSpinDate.getUTCFullYear(),
        lastSpinDate.getUTCMonth(),
        lastSpinDate.getUTCDate(),
        lastSpinDate.getUTCHours(),
        lastSpinDate.getUTCMinutes(),
        lastSpinDate.getUTCSeconds()
      );

      const nowUTC = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      );

      const diffHours = (nowUTC - lastSpinUTC) / (1000 * 60 * 60);
      
      if (diffHours < FREE_SPIN_COOLDOWN_HOURS) {
        // Calculate remaining time
        const remainingTotalSeconds = Math.floor((FREE_SPIN_COOLDOWN_HOURS - diffHours) * 3600);
        const remainingHours = Math.floor(remainingTotalSeconds / 3600);
        const remainingMinutes = Math.floor((remainingTotalSeconds % 3600) / 60);
        const remainingSeconds = remainingTotalSeconds % 60;
        
        const formattedTime = `${remainingHours.toString().padStart(2, '0')}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        setCooldownTimeRemaining(formattedTime);
        // If we already have a spin result, just show it again
        if (lastSpinResult) {
          let result: SpinResult = {
            success: true,
            message: 'Congratulations!',
            reward: {
              type: 'rp',
              amount: lastSpinResult,
              minPurchase: 0,
            }
          };
          setSpinResult(result);
        }
        return;
      }
    }
    
    if (!isSpinning) {
      // Clear previous results
      setSpinResult(null);
      
      // Select a random reward
      await selectRandomReward();
      
      // Implement the specific animation requirements from memory
      // 1. Disable CSS transition and reset rotation to 0
      setTransitionEnabled(false);
      setWheelRotation(0);
      
      // 2. Re-enable the transition after a small delay
      setTimeout(() => {
        setTransitionEnabled(true);
        
        // 3. Force a re-render with the transition enabled
        setTimeout(() => {
          // 4. Set the new rotation value to trigger the animation
          // Start spinning
          setMustSpin(true);
          setIsSpinning(true);
        }, 50);
      }, 50);
    }
  };

  const handleSpinStop = async () => {
    setMustSpin(false);
    setIsSpinning(false);
    
    // Get the winning segment
    const winner = wheelData[prizeNumber].option;
    
    // Create result object
    let result: SpinResult = {
      success: true,
      message: 'Congratulations!',
      reward: undefined
    };
    
    // If we have a selectedReward from our random selection, use that
    if (selectedReward) {
      result.reward = {
        type: selectedReward.rewardType,
        amount: selectedReward.rewardAmount,
        minPurchase: selectedReward.minPurchase
      };
      
      // Create free spin history record using the new endpoint
      if (telegramUser?.id && selectedReward.id) {
        try {
          const telegramId = typeof telegramUser.id === 'string' ? parseInt(telegramUser.id) : telegramUser.id;
          const response = await freeSpinWithReward();
          
          // Refresh last free spin data
          await refreshLastFreeSpin();
        } catch (error) {
          console.error('Error recording spin:', error);
          // If there's an error, we'll still show the result but log the error
        }
      }
      
      // Now show the result
      setSpinResult(result);
    } else {
      // This is a fallback in case something went wrong with our selection
      console.error('No selected reward found, trying to match by segment text');
      
      const winningReward = freeRewards.find(reward => {
        const segmentText = reward.rewardType === 'uc' 
          ? `${reward.rewardAmount} UC` 
          : `${reward.rewardAmount} рефбаллов`;
        
        const isMatch = segmentText === winner;
        return isMatch;
      });
      
      if (winningReward) {
        result.reward = {
          type: winningReward.rewardType,
          amount: winningReward.rewardAmount,
          minPurchase: winningReward.minPurchase
        };
        
        // Create spin history record and update bonus balance
        if (telegramUser?.id && winningReward.id) {
          try {
            const telegramId = typeof telegramUser.id === 'string' ? parseInt(telegramUser.id) : telegramUser.id;
            const response = await freeSpinWithReward();
            
            if (response.newBonusBalance !== undefined) {
              // Refresh user data to update the bonus balance in the context
              await refreshUser();
            }
          } catch (error) {
            console.error('Error recording spin:', error);
          }
        }
        
        setSpinResult(result);
      } else {
        console.error('NO PRIZE FOUND - This should never happen');
        result.success = false;
        result.message = 'Error: Could not determine prize';
        setSpinResult(result);
      }
    }
  };

  const renderSpinResult = () => {
    if (!spinResult || !spinResult.success || !spinResult.reward) {
      return null;
    }
    
    const reward = spinResult.reward;
    
    // Mock countdown time - in a real app, this would be calculated based on when the user can spin again
    // const countdownTime = '23:54:53';
    const countdownTime = cooldownTimeRemaining;
    
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(15, 34, 49, 0.85)', // Darker, more opaque background
          backdropFilter: 'blur(8px)', // Blur effect for the background
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
          padding: '20px',
        }}
        onClick={() => navigate('/paid-roulette')} // Redirect to roulette page on click
      >
      <Box
        sx={{
          width: '100%',
          maxWidth: '500px',
          textAlign: 'center',
          animation: 'fadeIn 0.5s ease-out',
          '@keyframes fadeIn': {
            from: { opacity: 0, transform: 'translateY(20px)' },
            to: { opacity: 1, transform: 'translateY(0)' }
          },
        }}
      >
        <Typography 
            variant="h3" 
            sx={{ 
              color: '#FFD700', // Gold color for celebration
              fontWeight: 'bold',
              marginBottom: '24px',
              textShadow: '0 2px 4px rgba(0,0,0,0.4)',
          }}
        >
            Юху!
        </Typography>
        
        <Typography 
            variant="h4" 
          sx={{ 
              color: '#FFFFFF',
              fontWeight: 'bold',
              marginBottom: '16px',
              lineHeight: 1.4,
              textShadow: '0 2px 4px rgba(0,0,0,0.4)',
            }}
          >
            Сегодня вы выиграли{' '}
            <Box component="span" sx={{ color: colors.primary.main, fontSize: '120%' }}>
              {reward.amount} рефбаллов
            </Box>
        </Typography>
        
          <Typography 
            variant="h5" 
            sx={{ 
              color: '#B0BEC5', // Lighter gray
              marginTop: '32px',
              fontWeight: 'normal',
            }}
          >
            Возвращайтесь через{' '}
            <Box component="span" sx={{ color: '#FFB74D', fontWeight: 'bold' }}>
              {countdownTime}
            </Box>
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              color: '#FFFFFF',
              marginTop: '40px',
              opacity: 0.7,
              fontSize: '14px',
            }}
          >
            Нажмите в любом месте, чтобы продолжить
          </Typography>
        </Box>
      </Box>
    );
  };

  const isReady = useMemo(
    () => wheelData.length > 0 && !loading,
    [wheelData, loading]
  );
  
  const actionButton = (
    <IconButton
      onClick={() => navigate('/bonus')}
      sx={{
        color: colors.text.primary,
        backgroundColor: colors.primary.main,
        '&:hover': {
          backgroundColor: colors.primary.dark,
        }
      }}
    >
      <AddCircleOutlineOutlinedIcon />
    </IconButton>
  );


  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: colors.background.default,
        }}
      >
        {/* Page Switcher Component */}
        <PageSwitcher
          pages={[
            { path: '/paid-roulette', label: 'Рулетка' },
            { path: '/free-wheel', label: 'Бонус' },
          ]}
        />
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        maxHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        overflow: 'auto', 
        paddingBottom: '160px', // Increased to account for fixed buttons
        position: 'relative', // Added for absolute positioning of children
        padding: theme.spacing(2),
        paddingTop: 0,
      }}
    >
      {/* Page Switcher Component - now sticky at top */}
      <PageSwitcher
        pages={[
          { path: '/paid-roulette', label: 'Рулетка' },
          { path: '/free-wheel', label: 'Бонус' },
        ]}
      />
      
        {/* PageHeader Component with action button */}
        <PageHeader
          // title="Бесплатное колесо"
          // subtitle="Крути и выигрывай рефбаллы"
          actionButton={actionButton}
        />
      
      {/* New Wheel Component with Arrow */}
      {isReady && (
        <>
          {/* Arrow positioned above the wheel */}
          <Box sx={{ 
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            marginBottom: '-10px',
            zIndex: 10,
            transform: 'rotate(-45deg)', 
          }}>
            {/* Detailed arrow SVG */}
            <svg 
              width="50" 
              height="50" 
              viewBox="0 0 700 700" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M0 0 C0.96421875 -0.03673828 1.9284375 -0.07347656 2.921875 -0.11132812 C10.22110178 -0.15595655 13.95778963 1.6555945 19.25 6.5625 C20.25546875 7.38492187 21.2609375 8.20734375 22.296875 9.0546875 C29.69234155 15.57381264 33.92887803 20.57001381 35.25 30.5625 C36.060302 45.45959064 34.07555546 57.44536712 24.06555176 69.10748291 C21.53320908 71.77137003 18.94781804 74.36729599 16.328125 76.9453125 C15.37668915 77.89435755 14.4259987 78.8441504 13.47601318 79.79464722 C11.50201966 81.76554099 9.52157997 83.72961783 7.53564453 85.68847656 C4.98754665 88.20226976 2.45314397 90.72935932 -0.07709885 93.26111031 C-2.03070609 95.2141753 -3.98946758 97.16199849 -5.95005035 99.10805893 C-7.34246266 100.49101502 -8.73188924 101.87697515 -10.12127686 103.26296997 C-11.38333954 104.5058931 -11.38333954 104.5058931 -12.67089844 105.77392578 C-13.41225037 106.50765411 -14.15360229 107.24138245 -14.91741943 107.99734497 C-16.75 109.5625 -16.75 109.5625 -18.75 109.5625 C-19.13671875 110.43648437 -19.13671875 110.43648437 -19.53125 111.328125 C-20.84834017 113.7427903 -22.30088384 115.37990177 -24.25 117.3125 C-24.8584375 117.92609375 -25.466875 118.5396875 -26.09375 119.171875 C-27.75 120.5625 -27.75 120.5625 -29.75 120.5625 C-29.75 121.2225 -29.75 121.8825 -29.75 122.5625 C-31.09666443 123.89039612 -31.09666443 123.89039612 -33.03295898 125.47875977 C-38.72295974 130.32182955 -43.9492229 135.61729327 -49.22265625 140.90234375 C-50.31531989 141.99433191 -51.40813178 143.08617175 -52.50108337 144.1778717 C-54.77944838 146.45477122 -57.05601783 148.73344653 -59.33129883 151.01342773 C-62.22809895 153.91592441 -65.1293668 156.81390706 -68.03200626 159.71056271 C-70.28593465 161.9608543 -72.53744726 164.21355092 -74.78826714 166.46695137 C-75.85630638 167.53565128 -76.92507108 168.60362674 -77.99461555 169.67082024 C-83.79439321 175.46064045 -89.43488161 181.32109422 -94.75 187.5625 C-103.21709288 197.06169081 -112.16912782 206.10741155 -121.13928223 215.12802124 C-122.38037503 216.37662508 -123.61987741 217.62681178 -124.85778809 218.87857056 C-126.61355468 220.65216025 -128.37600647 222.41881629 -130.140625 224.18359375 C-130.67651245 224.7266214 -131.2123999 225.26964905 -131.76452637 225.82913208 C-135.52086581 229.5625 -135.52086581 229.5625 -137.75 229.5625 C-138.08 230.5525 -138.41 231.5425 -138.75 232.5625 C-140.31640625 234.10890198 -140.31640625 234.10890198 -142.3125 235.75610352 C-146.67601247 239.46068532 -150.71955782 243.40383448 -154.75 247.46484375 C-155.4711499 248.1874086 -156.1922998 248.90997345 -156.93530273 249.6544342 C-159.20925415 251.93392604 -161.47967954 254.2168924 -163.75 256.5 C-165.30315235 258.05817506 -166.85653567 259.61611993 -168.41015625 261.17382812 C-172.19277586 264.96739759 -175.9725169 268.76381615 -179.75 272.5625 C-176.66770957 274.61736029 -174.83786572 275.10862078 -171.25 275.75 C-170.2909375 275.92917969 -169.331875 276.10835937 -168.34375 276.29296875 C-165.63809273 276.75441376 -165.63809273 276.75441376 -162.75 275.5625 C-158.46228098 275.28881581 -154.78087591 275.72838546 -150.57421875 276.578125 C-141.8184015 278.07610178 -133.06669512 277.65544555 -124.22070312 277.44604492 C-105.07318758 277.02755379 -105.07318758 277.02755379 -96.77832031 284.53540039 C-96.25334961 285.03934326 -95.72837891 285.54328613 -95.1875 286.0625 C-94.63030273 286.58642334 -94.07310547 287.11034668 -93.49902344 287.65014648 C-85.80902627 295.12155044 -80.68031585 302.8118261 -80.45727539 313.63378906 C-80.35723865 327.81677565 -81.69142439 338.73590507 -91.75 349.5625 C-94.68902092 352.50152092 -97.811762 351.87057334 -101.75341797 351.88134766 C-102.58995029 351.89137912 -103.42648262 351.90141058 -104.28836441 351.91174603 C-107.10629809 351.94294848 -109.92413371 351.95976786 -112.7421875 351.9765625 C-114.75422902 351.99558404 -116.76626322 352.015393 -118.77828979 352.03593445 C-124.25169332 352.08880795 -129.72511634 352.12840173 -135.19865417 352.16440201 C-138.61573265 352.18743753 -142.0327782 352.21328344 -145.4498291 352.24008179 C-158.54951235 352.34233978 -171.64922439 352.42714091 -184.74910736 352.49918842 C-193.24133601 352.54638379 -201.73345177 352.60378667 -210.22558594 352.66552734 C-220.67047576 352.74060839 -231.11531196 352.79851559 -241.56040764 352.83571243 C-247.06411937 352.85531289 -252.56763123 352.88079961 -258.07122803 352.92193604 C-263.24752153 352.95996535 -268.42352028 352.97828595 -273.59994888 352.98157692 C-275.49280807 352.98597653 -277.38566616 352.99716253 -279.27843857 353.01581383 C-290.0413243 353.11704261 -299.19720115 353.18509375 -308.3359375 346.73828125 C-308.92632812 346.12339844 -309.51671875 345.50851562 -310.125 344.875 C-311.03378906 343.97201172 -311.03378906 343.97201172 -311.9609375 343.05078125 C-318.56198977 333.86984173 -318.3060646 324.76038883 -318.2199707 313.88134766 C-318.22048005 312.09180575 -318.22273928 310.30226362 -318.22660828 308.51272583 C-318.23200861 303.67421712 -318.21266984 298.83609405 -318.18850732 293.99765444 C-318.16716114 288.9302012 -318.16780421 283.86274862 -318.16581726 278.79525757 C-318.15937911 270.29328805 -318.13816251 261.7914629 -318.10668945 253.28955078 C-318.06632178 242.37040597 -318.04898878 231.45135494 -318.03938007 220.53214455 C-318.03108623 211.1439719 -318.0138525 201.75581916 -317.99434257 192.36766386 C-317.99029227 190.35224068 -317.98655291 188.33681686 -317.9831202 186.32139254 C-317.97334324 180.58164622 -317.95887096 174.84195623 -317.9362812 169.10224533 C-317.93015257 167.36194113 -317.92570583 165.62163009 -317.92303848 163.88131714 C-317.91906471 161.51154199 -317.90895434 159.14187256 -317.89717102 156.77212524 C-317.89735538 156.07885026 -317.89753975 155.38557527 -317.89772969 154.67129195 C-317.87673995 151.68421778 -317.70343845 149.42281536 -316.75 146.5625 C-315.76 146.5625 -314.77 146.5625 -313.75 146.5625 C-314.08 145.9025 -314.41 145.2425 -314.75 144.5625 C-315.36228519 141.34800277 -315.25554227 139.35009787 -313.46875 136.56640625 C-307.6897283 129.61844607 -300.97137026 125.94777011 -292.75 122.5625 C-291.265 121.5725 -291.265 121.5725 -289.75 120.5625 C-279.98260576 119.22590921 -272.74700063 120.84612548 -264.75 126.5625 C-262.75 128.5625 -262.75 128.5625 -262.75 131.5625 C-261.85788818 131.71658325 -261.85788818 131.71658325 -260.94775391 131.8737793 C-258.38129764 132.67804203 -257.39332661 133.61590424 -255.5859375 135.58203125 C-255.02132812 136.19240234 -254.45671875 136.80277344 -253.875 137.43164062 C-253.2975 138.07294922 -252.72 138.71425781 -252.125 139.375 C-251.24714844 140.31956055 -251.24714844 140.31956055 -250.3515625 141.28320312 C-246.19119883 145.83002259 -243.47431654 149.01119435 -243.5546875 155.40625 C-243.56113281 156.32277344 -243.56757813 157.23929688 -243.57421875 158.18359375 C-243.59935547 159.60865234 -243.59935547 159.60865234 -243.625 161.0625 C-243.63402344 162.01253906 -243.64304687 162.96257812 -243.65234375 163.94140625 C-243.66974657 165.70903527 -243.69126381 167.47663211 -243.71826172 169.24414062 C-243.75738838 172.10219281 -243.75942257 174.95840886 -243.71484375 177.81640625 C-243.70582031 178.57824219 -243.69679688 179.34007812 -243.6875 180.125 C-243.67589844 180.88941406 -243.66429687 181.65382813 -243.65234375 182.44140625 C-243.75 184.5625 -243.75 184.5625 -244.75 187.5625 C-244.42 188.2225 -244.09 188.8825 -243.75 189.5625 C-242.86283519 199.95980276 -242.96765732 210.7464202 -246.75 220.5625 C-241.0283336 216.67176685 -236.1879961 212.19296476 -231.3125 207.3125 C-230.37156494 206.37253174 -230.37156494 206.37253174 -229.41162109 205.41357422 C-226.18761071 202.27902019 -226.18761071 202.27902019 -223.75 198.5625 C-223.09 198.5625 -222.43 198.5625 -221.75 198.5625 C-221.75 197.9025 -221.75 197.2425 -221.75 196.5625 C-221.09 196.5625 -220.43 196.5625 -219.75 196.5625 C-219.49726318 195.98669189 -219.24452637 195.41088379 -218.98413086 194.81762695 C-216.60932471 190.47814438 -212.78924642 187.22078227 -209.3125 183.75 C-208.49861816 182.9355542 -207.68473633 182.1211084 -206.84619141 181.28198242 C-201.86410356 176.39039706 -197.14128263 172.3720338 -191.06640625 168.921875 C-187.17559325 166.63856484 -184.01282009 163.65076593 -180.75 160.5625 C-180.08226562 159.96953125 -179.41453125 159.3765625 -178.7265625 158.765625 C-174.87596587 155.19534 -172.2279771 151.80679566 -169.81167603 147.13819885 C-165.30797536 139.28677931 -159.22975387 133.36197765 -152.82568359 127.03881836 C-151.52137692 125.73264395 -150.21815904 124.42538154 -148.91593933 123.11712646 C-145.40469201 119.59597244 -141.87820234 116.09046904 -138.34833097 112.58799815 C-134.64669837 108.91032481 -130.95858518 105.21913835 -127.26870728 101.52967834 C-121.07849106 95.34407719 -114.8788786 89.16802705 -108.67285156 82.99829102 C-101.50106898 75.86833585 -94.34152252 68.72628346 -87.18897569 61.57703489 C-79.56006799 53.95172654 -71.92355352 46.33407473 -64.28412676 38.71930623 C-62.08706202 36.52846509 -59.89129133 34.33633368 -57.69575119 32.14396477 C-54.24825247 28.70236843 -50.7955953 25.26602955 -47.33887672 21.83369446 C-46.07178401 20.57403676 -44.80636428 19.31269363 -43.5427494 18.0495472 C-41.81753125 16.32558846 -40.08629357 14.60790948 -38.35336304 12.89170837 C-37.85520596 12.39113621 -37.35704888 11.89056405 -36.84379613 11.37482303 C-30.61565566 5.24052493 -26.35974943 4.2060075 -17.80859375 3.96875 C-14.75571542 3.82929886 -14.75571542 3.82929886 -12.86645508 2.06860352 C-9.03614868 -0.6571042 -4.55610099 0.0089923 0 0 Z " 
                fill="#FDE839" 
                stroke='#333333'
                strokeWidth={1}
                transform="translate(319.75,277.4375)"
              />
            </svg>
          </Box>
          
          <Box 
            sx={{ 
              transform: 'rotate(-45deg)', 
              transformOrigin: 'center' // Controls the pivot point of rotation
            }}
          >
            <Wheel
              mustStartSpinning={mustSpin}
              prizeNumber={prizeNumber}
              data={wheelData}
              onStopSpinning={handleSpinStop}
              backgroundColors={[wheelColors.primary, wheelColors.secondary]}
              textColors={['white']}
              // fontFamily='Roboto'
              // fontWeight={100}
              // fontStyle='bold'
              fontSize={16}
              outerBorderColor={wheelColors.border}
              outerBorderWidth={7}
              // innerRadius={3}
              // innerBorderColor={wheelColors.border}
              // innerBorderWidth={7}
              radiusLineColor={wheelColors.border}
              radiusLineWidth={1}
              spinDuration={0.8}
              perpendicularText={false}
              // textDistance={60}
              pointerProps={{ src: "/empty.png" }}
            />
          </Box>
        </>
      )}

      {/* Fixed button container at the bottom */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '16px',
          backgroundColor: 'rgba(19, 47, 62, 0.9)', // Slightly transparent background
          backdropFilter: 'blur(8px)',
          borderTop: `1px solid ${colors.divider}`,
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          zIndex: 10,
        }}
      >
        <Button
          variant="contained" 
          color="primary" 
          size="large"
          onClick={handleSpinClick}
          disabled={isSpinning}
          sx={{
            width: '100%',
            padding: '12px',
            borderRadius: '12px',
            fontSize: isMobile ? '14px' : '16px',
            fontWeight: 500,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: colors.primary.dark,
            }
          }}
        >
          {isSpinning ? 'Хеликоптер-хеликоптер...' : 'Крутить'}
        </Button>
      </Box>
      
      {renderSpinResult()}
    </Box>
  );
};

export default FreeWheelPage;
